'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, LogIn, Loader2, CheckCircle2 } from 'lucide-react';

type Role = 'admin' | 'investor';

interface FormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

const demoCredentials: { role: Role; label: string; email: string; password: string }[] = [
  { role: 'admin', label: 'Admin', email: 'siosaia@vatulend.vu', password: 'Admin@VL2026' },
  { role: 'investor', label: 'Investor', email: 'timoci@vatulend.vu', password: 'Invest@VL2026' },
];

export default function LoginForm() {
  const router = useRouter();
  const [selectedRole, setSelectedRole] = useState<Role>('admin');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleUseCredentials = (cred: typeof demoCredentials[0]) => {
    setSelectedRole(cred.role);
    setValue('email', cred.email);
    setValue('password', cred.password);
    clearErrors();
  };

  // Backend integration point: replace with real auth call to /api/auth/login
  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));

    const valid = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );

    if (!valid) {
      setIsLoading(false);
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }

    setIsLoading(false);
    router.push('/');
  };

  return (
    <div className="w-full max-w-md">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <span className="text-primary-foreground font-bold text-sm">VL</span>
        </div>
        <span className="text-lg font-bold text-foreground">VatuLend</span>
      </div>

      <div className="mb-7">
        <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your VatuLend account</p>
      </div>

      {/* Role selector */}
      <div className="flex rounded-xl bg-muted p-1 mb-6 gap-1">
        {demoCredentials.map((cred) => (
          <button
            key={`role-tab-${cred.role}`}
            type="button"
            onClick={() => {
              setSelectedRole(cred.role);
              clearErrors();
            }}
            className={[
              'flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200',
              selectedRole === cred.role
                ? 'bg-card text-foreground shadow-card'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {cred.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Root error */}
        {errors.root && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-negative-subtle border border-negative/20 fade-in">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-negative shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
            <p className="text-xs text-negative leading-relaxed">{errors.root.message}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-xs font-semibold text-foreground mb-1.5">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@vatulend.vu"
            className={`input-base w-full px-3.5 py-2.5 text-sm ${errors.email ? 'border-negative' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-xs text-negative mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-xs font-semibold text-foreground mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••"
              className={`input-base w-full px-3.5 py-2.5 pr-10 text-sm ${errors.password ? 'border-negative' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-negative mt-1">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="rememberMe"
            type="checkbox"
            className="w-4 h-4 rounded border-border bg-input accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-xs text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              <LogIn size={16} />
              <span>Sign In as {selectedRole === 'admin' ? 'Admin' : 'Investor'}</span>
            </>
          )}
        </button>
      </form>

      {/* Demo credentials */}
      <div className="mt-6 p-4 rounded-xl bg-muted border border-border">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Demo Credentials
        </p>
        <div className="space-y-2">
          {demoCredentials.map((cred) => (
            <div
              key={`cred-${cred.role}`}
              className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-background border border-border hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cred.role === 'admin' ? 'badge-pending' : 'badge-paid'}`}>
                  {cred.label}
                </span>
                <span className="text-xs font-mono text-muted-foreground truncate">{cred.email}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                  className="btn-ghost p-1.5 rounded-md"
                  aria-label={`Copy ${cred.label} email`}
                >
                  {copiedField === `${cred.role}-email` ? (
                    <CheckCircle2 size={12} className="text-positive" />
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleUseCredentials(cred)}
                  className="text-[11px] font-semibold text-primary hover:underline px-1.5 py-1 rounded transition-colors"
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2.5">
          Click &quot;Use&quot; to autofill the form above, then sign in.
        </p>
      </div>

      <p className="text-[11px] text-muted-foreground text-center mt-6">
        VatuLend v2.4 · Private Lending Management · Port Vila, Vanuatu
      </p>
    </div>
  );
}